import { useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { useSurveyStore } from '@/stores/survey-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useResponseStore } from '@/stores/response-store';
import { useUIStore } from '@/stores/ui-store';
import { QuestionRenderer } from '@/components/questions';
import type { Question, AnswerValue } from '@/types';

interface QuestionPageProps {
  className?: string;
}

export function QuestionPage({ className }: QuestionPageProps) {
  const { pages, getVisibleQuestions } = useSurveyStore();
  const { currentPage, hiddenQuestions, displayOrders } = useNavigationStore();
  const { answers, setAnswer, startQuestion } = useResponseStore();
  const { validationErrors, clearValidationError } = useUIStore();

  // Get visible questions for current page
  const visibleQuestions = useMemo(() => {
    return getVisibleQuestions(currentPage, hiddenQuestions);
  }, [currentPage, hiddenQuestions, getVisibleQuestions]);

  // Get current page info
  const currentPageInfo = useMemo(() => {
    return pages.find((p) => p.order === currentPage);
  }, [pages, currentPage]);

  // Handle answer change
  const handleAnswerChange = (question: Question, value: AnswerValue) => {
    setAnswer(question.code, question.id, value);
    // Dismiss a stale "required"/format error as soon as the respondent edits;
    // the page re-validates on Next, so a still-invalid answer re-flags there.
    if (validationErrors[question.code]?.length) {
      clearValidationError(question.code);
    }
  };

  // Handle question focus (for timing)
  const handleQuestionFocus = (question: Question) => {
    startQuestion(question.code, question.id);
  };

  if (visibleQuestions.length === 0) {
    return (
      <div className={cn('animate-slide-up', className)}>
        <p className="text-center text-gray-500">
          No questions on this page.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Page title */}
      {currentPageInfo?.title && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900">
            {currentPageInfo.title}
          </h2>
          {currentPageInfo.description && (
            <p className="mt-2 text-gray-600">{currentPageInfo.description}</p>
          )}
        </div>
      )}

      {/* Questions */}
      {visibleQuestions.map((question, index) => (
        <div
          key={question.id}
          className="animate-slide-up"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <QuestionRenderer
            question={question}
            value={answers[question.code] ?? null}
            onChange={(value) => handleAnswerChange(question, value)}
            onFocus={() => handleQuestionFocus(question)}
            errors={validationErrors[question.code]}
            displayOrder={displayOrders[question.code]}
          />
        </div>
      ))}
    </div>
  );
}
